"use client";

import { motion, useTransform } from "framer-motion";

import {
  depthOf,
  depthOpacity,
  depthScale,
  depthShade,
  depthYPercent,
  depthZ,
} from "./deckLayout";

// One positioned slot in the deck. Owns its own transforms so the hooks are
// called once per instance — the parent just maps over the clients and never
// calls hooks in a loop.
//
// `p` is the deck position as a MotionValue: whole numbers put a card at the
// front, and everything between is interpolated. What moves `p` is the caller's
// business (scroll on desktop, a finger on touch), which is why both decks can
// share this file.
//
// Fills its parent, so the parent is what defines the card box.
export default function DeckLayer({ index, count, p, children, horizontal = false, entered = true, onEntered }) {
  const depth = useTransform(p, (value) => depthOf(index, value, count));

  // A continuous orbit keeps the current desktop card centred and places
  // its neighbours symmetrically behind it, including when wrapping 3 -> 1.
  const orbit = useTransform(p, value => (index - value) * Math.PI * 2 / count);
  const scale = useTransform(() => horizontal
    ? 0.86 + 0.14 * (Math.cos(orbit.get()) + 1) / 2
    : depthScale(depth.get()));
  const scaleY = useTransform(() => horizontal
    ? 0.68 + 0.32 * (Math.cos(orbit.get()) + 1) / 2
    : depthScale(depth.get()));
  const x = useTransform(orbit, angle => horizontal ? `${Math.sin(angle) * 16}%` : "0%");
  const verticalY = useTransform(depth, d => horizontal ? "0%" : `${depthYPercent(d)}%`);
  const opacity = useTransform(depth, d => horizontal ? 1 : depthOpacity(d, count));
  const zIndex = useTransform(() => horizontal
    ? Math.round(100 + Math.cos(orbit.get()) * 10)
    : depthZ(depth.get()));
  const filter = useTransform(() => `brightness(${horizontal
    ? 0.75 + 0.25 * (Math.cos(orbit.get()) + 1) / 2
    : 1 - depthShade(depth.get())})`);

  // Only the front card takes clicks — otherwise the cards stacked behind, or
  // the one on its way out (which sits ON TOP while it fades), would swallow
  // taps meant for the front card's chips.
  //
  // The window is centred on the front slot and exactly one depth level wide,
  // so precisely one card ever qualifies: depths are always a whole number
  // apart, so no two can be inside it at once. It has to reach into the
  // NEGATIVE half as well, because depth goes negative the instant a card
  // starts leaving. A [0, 0.5) window looks equivalent but is not — it only
  // covers a card's approach, and the FIRST card has no approach: it starts
  // life at the front, so its depth is already negative one pixel into the
  // section and its chips were dead for the entire deck.
  const pointerEvents = useTransform(depth, (d) =>
    d >= -0.5 && d < 0.5 ? "auto" : "none",
  );

  return (
    <motion.div
      style={{ scaleX: scale, scaleY, x, y: verticalY, opacity, zIndex, pointerEvents, filter }}
      className="absolute inset-0"
    >
      <motion.div
        className="h-full w-full"
        initial={{ opacity: 0, x: horizontal ? "100%" : 0, y: horizontal ? 0 : 100 }}
        animate={entered ? { opacity: 1, x: 0, y: 0 } : undefined}
        onAnimationComplete={() => { if (entered && index === 0) onEntered?.(); }}
        transition={{ duration: 0.8, delay: (count - 1 - index) * 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
